import { t as uiText } from '../i18n';
// src/utils/userUtils.ts
import { db } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';

export interface UserInfo {
  id: string;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
}

// ユーザー情報のキャッシュ
const userCache = new Map<string, UserInfo>();

/**
 * ユーザーIDからユーザー情報を取得する
 * @param userId ユーザーID
 * @returns ユーザー情報
 */
export const getUserInfo = async (userId: string): Promise<UserInfo | null> => {
  // キャッシュから取得を試行
  if (userCache.has(userId)) {
    return userCache.get(userId)!;
  }

  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const userData = userSnap.data();
      const userInfo: UserInfo = {
        id: userId,
        displayName: userData.displayName || null,
        email: userData.email || null,
        photoURL: userData.photoURL || null,
      };

      // キャッシュに保存
      userCache.set(userId, userInfo);
      return userInfo;
    } else {
      console.warn(`ユーザー ${userId} が見つかりません`);
      return null;
    }
  } catch (error) {
    console.error(`ユーザー情報の取得に失敗しました (${userId}):`, error);
    return null;
  }
};

/**
 * 複数のユーザーIDからユーザー情報を一括取得する
 * @param userIds ユーザーIDの配列
 * @returns ユーザー情報の配列
 */
export const getMultipleUserInfo = async (userIds: string[]): Promise<UserInfo[]> => {
  const uniqueUserIds = Array.from(new Set(userIds)); // 重複を除去
  const userInfos: UserInfo[] = [];

  // 並列でユーザー情報を取得
  const promises = uniqueUserIds.map(userId => getUserInfo(userId));
  const results = await Promise.all(promises);

  // nullでない結果のみを返す
  results.forEach(result => {
    if (result) {
      userInfos.push(result);
    }
  });

  return userInfos;
};

/**
 * ユーザー情報のキャッシュをクリアする
 */
export const clearUserCache = (): void => {
  userCache.clear();
};

/**
 * 特定のユーザーのキャッシュをクリアする
 * @param userId ユーザーID
 */
export const clearUserCacheById = (userId: string): void => {
  userCache.delete(userId);
};

/**
 * ユーザー名を表示用にフォーマットする
 * @param userInfo ユーザー情報
 * @returns 表示用のユーザー名
 */
export const formatUserName = (userInfo: UserInfo | null): string => {
  if (!userInfo) return uiText("Unknown user");
  
  if (userInfo.displayName) {
    return userInfo.displayName;
  } else if (userInfo.email) {
    return userInfo.email;
  } else {
    return uiText("User");
  }
};
